import { useSearchParams } from 'react-router-dom'
import { useOrgContext } from './context/organization/org-context'

/**
 * Resolves the active seasonId from multiple sources in priority order:
 * 1. URL param (highest priority)
 * 2. OrgContext lastSelectedSeasonId (current session)
 * 3. localStorage keyed by farmId (cross-session, per-farm)
 * 4. Active season (state === 'active')
 * 5. First season in list
 *
 * @param {{ farmId: string|null, seasons: Array, loading: boolean }}
 * @returns {{ seasonId: string|null, loading: boolean, source: string|null }}
 */
export const useSeasonId = ({ farmId, seasons = [], loading = false }) => {
  const [searchParams] = useSearchParams()
  const { lastSelectedSeasonId } = useOrgContext()

  const urlSeasonId = searchParams.get('seasonId')

  if (loading) {
    // Trust the URL value while seasons are still being fetched
    return { seasonId: urlSeasonId ?? null, loading: true, source: urlSeasonId ? 'url' : null }
  }

  if (seasons.length === 0) {
    return { seasonId: null, loading: false, source: null }
  }

  // Priority 1: URL params — only if the season actually exists for this farm
  if (urlSeasonId && seasons.some(s => s.id === urlSeasonId)) {
    return { seasonId: urlSeasonId, loading: false, source: 'url' }
  }

  // Priority 2: Context (last selected in current session)
  if (lastSelectedSeasonId && seasons.some(s => s.id === lastSelectedSeasonId)) {
    return { seasonId: lastSelectedSeasonId, loading: false, source: 'context' }
  }

  // Priority 3: localStorage keyed by farmId
  if (farmId) {
    const stored = localStorage.getItem(`lastSeasonId_${farmId}`)
    if (stored && seasons.some(s => s.id === stored)) {
      return { seasonId: stored, loading: false, source: 'localStorage' }
    }
  }

  // Priority 4: Active season
  const activeSeason = seasons.find(s => s.state === 'active')
  if (activeSeason) {
    return { seasonId: activeSeason.id, loading: false, source: 'active' }
  }

  // Priority 5: First season
  return { seasonId: seasons[0]?.id ?? null, loading: false, source: 'default' }
}
